// src/components/Optimization/ValueBridge.tsx
import React, { useState, useMemo } from 'react';
import { Box, Typography, Paper, Grid, Tooltip } from '@mui/material';

interface TacticalDataPoint {
  label: string;
  value: number;
  formattedValue: string;
}

interface ValueBridgeProps {
  base_historical_solid_cp: number;
  calculated_suggested_margin: number;
  mei_markup_mp_plus_mde: number;
  applied_strategy_profile: string;
  tacticalIntelligence: Record<string, string> | null;
}

const parseTacticalData = (tacticalIntel: Record<string, string> | null): TacticalDataPoint[] => {
  if (!tacticalIntel) return [];
  return Object.entries(tacticalIntel).map(([label, valueStr]) => {
    const numericValue = parseFloat(valueStr.replace('%', '')) / 100;
    return { label, value: numericValue, formattedValue: valueStr };
  }).filter(item => !isNaN(item.value));
};

const ValueBridge: React.FC<ValueBridgeProps> = ({
  base_historical_solid_cp,
  calculated_suggested_margin,
  mei_markup_mp_plus_mde,
  tacticalIntelligence,
}) => {
  const [isHovered, setIsHovered] = useState(false);

  const formatPercent = (val: number) => `${(val * 100).toFixed(1)}%`;

  const totalOpportunity = mei_markup_mp_plus_mde - base_historical_solid_cp;
  const capturedOpportunity = calculated_suggested_margin - base_historical_solid_cp;
  const capturedFlex = totalOpportunity > 0 ? (capturedOpportunity / totalOpportunity) * 100 : 0;
  const remainingFlex = totalOpportunity > 0 ? 100 - capturedFlex : 0;
  
  const allTacticalData = useMemo(() => parseTacticalData(tacticalIntelligence), [tacticalIntelligence]);
  const { inRange } = useMemo(() => {
    const inRange: TacticalDataPoint[] = [];
    allTacticalData.forEach(item => {
      if (item.value >= base_historical_solid_cp && item.value <= mei_markup_mp_plus_mde) {
        inRange.push(item);
      }
    });
    return { inRange };
  }, [allTacticalData, base_historical_solid_cp, mei_markup_mp_plus_mde]);

  const getPositionPercent = (value: number) => {
    if (totalOpportunity <= 0) return 0;
    return ((value - base_historical_solid_cp) / totalOpportunity) * 100;
  };

  return (
    // CAMBIO: Estilo Dark Premium minimalista (Sin sombra, borde sutil)
    <Paper elevation={0} sx={{ 
        p: 3, 
        mt: 4, 
        bgcolor: 'rgba(30, 41, 59, 0.4)', // Fondo semitransparente oscuro
        border: '1px solid #334155',
        borderRadius: 2
    }}>
      <Typography variant="subtitle2" sx={{ mb: 3, color: '#94a3b8', textAlign: 'center', letterSpacing: 1, textTransform: 'uppercase' }}>
        PUENTE DE VALOR
      </Typography>

      <Box 
        sx={{ width: '100%', mb: 2, position: 'relative' }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <Box sx={{ display: 'flex', height: '12px', borderRadius: '6px', overflow: 'hidden', bgcolor: 'rgba(255,255,255,0.05)' }}>
          <Box sx={{ flexBasis: `${capturedFlex}%`, bgcolor: '#38bdf8', transition: 'flex-basis 0.5s' }} /> {/* Cyan */}
          <Box sx={{ flexBasis: `${remainingFlex}%`, bgcolor: 'rgba(56, 189, 248, 0.2)', transition: 'flex-basis 0.5s' }} /> {/* Cyan apagado */}
        </Box>
        
        {isHovered && inRange.map(item => (
          <Tooltip key={item.label} title={`${item.label}: ${item.formattedValue}`} placement="top" arrow>
            <Box sx={{
              position: 'absolute', top: '-4px', bottom: '-4px',
              left: `${getPositionPercent(item.value)}%`,
              width: '2px', bgcolor: '#fff', cursor: 'pointer',
            }}/>
          </Tooltip>
        ))}
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2, px: 1 }}>
        <Box sx={{ textAlign: 'left' }}>
            <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mb: 0.5 }}>BASE</Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#94a3b8' }}>{formatPercent(base_historical_solid_cp)}</Typography>
        </Box>
        <Box sx={{ textAlign: 'center' }}>
            <Typography variant="caption" sx={{ color: '#38bdf8', display: 'block', mb: 0.5 }}>NUEVO MARGEN</Typography>
            <Typography variant="h5" sx={{ fontWeight: 800, color: '#38bdf8' }}>{formatPercent(calculated_suggested_margin)}</Typography>
        </Box>
        <Box sx={{ textAlign: 'right' }}>
            <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mb: 0.5 }}>POTENCIAL</Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#94a3b8' }}>{formatPercent(mei_markup_mp_plus_mde)}</Typography>
        </Box>
      </Box>

      <Grid container spacing={2} sx={{ mt: 3, pt: 3, borderTop: '1px solid rgba(255,255,255,0.05)' }}>
        <Grid item xs={6} sx={{ textAlign: 'center', borderRight: '1px solid rgba(255,255,255,0.05)' }}>
            <Typography variant="caption" sx={{ color: '#64748b' }}>Oportunidad Total</Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#f8fafc' }}>
              {totalOpportunity >= 0 ? '+' : ''}{(totalOpportunity * 100).toFixed(2)} pp
            </Typography>
        </Grid>
        <Grid item xs={6} sx={{ textAlign: 'center' }}>
            <Typography variant="caption" sx={{ color: '#64748b' }}>Impacto Estrategia</Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#38bdf8' }}>
              {capturedOpportunity >= 0 ? '+' : ''}{(capturedOpportunity * 100).toFixed(2)} pp
            </Typography>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default ValueBridge;